/**
 * Yjs CodeMirror Extension Factory
 *
 * Creates a CodeMirror extension for Yjs CRDT-based real-time collaboration.
 * Wraps y-codemirror.next's yCollab with proper configuration.
 *
 * @module features/collab/yjs/createYjsExtension
 * @see SPEC-008 Requirements 1.1, 1.2, 1.3, 1.4
 */

import type * as Y from 'yjs';
import { yCollab } from 'y-codemirror.next';
import type { Extension } from '@codemirror/state';
import type { Awareness } from 'y-protocols/awareness';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for creating Yjs extension
 */
export interface YjsExtensionOptions {
  /**
   * The Y.Doc instance to bind to the editor.
   * Must contain a Y.Text with the name 'content'.
   */
  yDoc: Y.Doc;

  /**
   * Optional awareness instance for presence (cursors, selections).
   * If not provided, presence features are disabled.
   */
  awareness?: Awareness;

  /**
   * Optional UndoManager for collaborative undo/redo.
   * IMPORTANT: Configure trackedOrigins to only track local edits.
   *
   * @example
   * ```typescript
   * const undoManager = new Y.UndoManager(yText, {
   *   captureTimeout: 500,
   *   trackedOrigins: new Set([null]), // Only track local edits
   * });
   * ```
   */
  undoManager?: Y.UndoManager;
}

/**
 * Result from creating Yjs extension
 */
export interface YjsExtensionResult {
  /**
   * CodeMirror extension to add to EditorState.create()
   */
  extension: Extension;

  /**
   * Reference to the Y.Text being synced
   */
  yText: Y.Text;

  /**
   * Cleanup function to call on unmount.
   * Destroys UndoManager if one was provided.
   */
  destroy: () => void;
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create Yjs CodeMirror extension for real-time collaboration.
 *
 * This function wraps y-codemirror.next's yCollab extension with proper
 * configuration for Linkode's collaboration system.
 *
 * @param options - Configuration options
 * @returns Extension result with extension, yText reference, and cleanup
 *
 * @example
 * ```typescript
 * const yDoc = syncManager.getDoc();
 * const yText = yDoc.getText('content');
 *
 * // Configure UndoManager: only track local edits, not remote collaboration
 * const undoManager = new Y.UndoManager(yText, {
 *   captureTimeout: 500,
 *   trackedOrigins: new Set([null]),
 * });
 *
 * const { extension, destroy } = createYjsExtension({
 *   yDoc,
 *   awareness: roomConnection.awareness,
 *   undoManager,
 * });
 *
 * const state = EditorState.create({
 *   doc: yText.toString(),
 *   extensions: [basicSetup, markdown(), extension],
 * });
 *
 * const view = new EditorView({ state, parent: container });
 *
 * // On unmount:
 * destroy();
 * view.destroy();
 * ```
 */
export function createYjsExtension(options: YjsExtensionOptions): YjsExtensionResult {
  const { yDoc, awareness, undoManager } = options;

  // Get or create the Y.Text named 'content'
  const yText = yDoc.getText('content');

  // Create yCollab extension
  // yCollab returns a CodeMirror Extension that syncs the editor with Y.Text
  // It also handles:
  // - Remote cursor/selection rendering (if awareness is provided)
  // - Undo/redo integration (if undoManager is provided)
  // Note: yCollab expects undoManager to be UndoManager | false, not undefined
  const extension = yCollab(yText, awareness, {
    undoManager: undoManager ?? false,
  });

  return {
    extension,
    yText,
    destroy() {
      // Destroy UndoManager if provided
      // Note: EditorView.destroy() handles yCollab cleanup internally
      undoManager?.destroy();
    },
  };
}
